import type {IPropertiesMap} from "@buildwithflux/models";
import {Records} from "@buildwithflux/shared";

import type {DocumentSelector, DocumentStoreState, ParameterizedSelector} from "../types";
import {emptyArray} from "../utils";

/**
 * Base selectors for document properties - direct state reads
 * These selectors perform simple property access without complex computation
 */

/** Gets all document properties */
export const all: DocumentSelector<IPropertiesMap | undefined> = (state: DocumentStoreState) => state.properties;

/** Gets a property by UID */
export const byUid: ParameterizedSelector<string, IPropertiesMap[string] | undefined> = (uid: string) => {
    return (state: DocumentStoreState) => state.properties?.[uid];
};

/** Gets multiple properties by UIDs */
export const byUids: ParameterizedSelector<string[], Array<IPropertiesMap[string]>> = (uids: string[]) => {
    return (state: DocumentStoreState) => {
        const properties = state.properties;
        if (!properties) return [];
        const result: Array<IPropertiesMap[string]> = [];
        for (const uid of uids) {
            const property = properties[uid];
            if (property) {
                result.push(property);
            }
        }
        return result;
    };
};

/** Gets all property UIDs */
export const uids: DocumentSelector<string[]> = (state: DocumentStoreState) => {
    const properties = state.properties;
    if (!properties) return emptyArray;
    return Object.keys(properties);
};

/** Counts total properties */
export const count: DocumentSelector<number> = (state: DocumentStoreState) => {
    const properties = state.properties;
    if (!properties) return 0;
    return Records.count(properties);
};

/** Checks if document has any properties */
export const hasProperties: DocumentSelector<boolean> = (state: DocumentStoreState) => {
    const properties = state.properties;
    if (!properties) return false;
    return !Records.isEmpty(properties);
};
